import React from "react";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import { CompanyCardData } from "./types";

interface Props {
  data: Pick<
    CompanyCardData,
    | "name"
    | "ticker"
    | "sector"
    | "exchange"
    | "founded"
    | "logo_initials"
    | "price"
    | "currency"
    | "price_change_pct"
    | "price_change_positive"
    | "one_liner"
  >;
  selectedStock: any;
}

// "2026-10-02" → "2 Oct 2026". Parsed by parts so the viewer's time zone
// can't shift it to the previous day.
const formatTradedDate = (value?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  if (!match) return "";
  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const CompanyHeader: React.FC<Props> = ({ data, selectedStock }) => {
  return (
    <div className="vi-glass-card p-6 mb-5">
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-[14px] bg-primary/10 text-primary flex items-center justify-center font-bold text-base flex-shrink-0">
          <img
            src={`/${selectedStock.company_logo}`}
            alt={data.logo_initials}
            className="w-50 object-contain"
          />
        </div>

        {/* Name + tag */}
        <div className="flex-1 min-w-0">
          <h1 className="font-serif text-[26px] font-semibold text-[#23312E] leading-tight tracking-tight m-0">
            {data.name}
          </h1>
          <p className="text-[13.5px] text-[#5C6B66] mt-0.5 m-0">
            {data.sector} · Listed on the {data.exchange} ({data.ticker}) ·
            Founded {data.founded}
          </p>
        </div>

        {/* Price */}
        {/* Price */}
        <div className="text-right flex-shrink-0">
          <div className="font-serif text-[28px] font-semibold text-[#23312E] leading-none">
            {selectedStock.closed}{" "}
            <span className="text-base font-sans text-[#5C6B66]">
              {data.currency}
            </span>
          </div>
          <div
            className={`text-[13px] font-semibold mt-1 flex items-center justify-end gap-0.5 ${
              Number(selectedStock.change_perc) < 0
                ? "text-red-500"
                : "text-[#0E6E5E]"
            }`}
          >
            {Number(selectedStock.change_perc) < 0 ? (
              <ArrowDownOutlined style={{ fontSize: 12 }} />
            ) : (
              <ArrowUpOutlined style={{ fontSize: 12 }} />
            )}
            {selectedStock.change_perc}%
            {formatTradedDate(selectedStock.last_traded_date) &&
              ` on ${formatTradedDate(selectedStock.last_traded_date)}`}
          </div>
        </div>
      </div>

      {/* One-liner */}
      <div className="mt-5 bg-[#E4EEF6] rounded-[14px] px-4 py-4">
        <p className="text-[11px] uppercase tracking-[0.09em] text-[#1F5E8C] font-bold m-0 mb-1">
          In one line
        </p>
        <p className="text-[16.5px] text-[#23312E] m-0">{data.one_liner}</p>
      </div>
    </div>
  );
};

export default CompanyHeader;
